import { Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef } from 'react';

const GoogleReviewsOnly = () => {
  const [reviewData, setReviewData] = useState({
    reviews: [],
    totalReviews: 236,
    averageRating: 4.9,
    lastUpdated: ''
  });
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const textRefs = useRef<(HTMLQuoteElement | null)[]>([]);
  const [canExpand, setCanExpand] = useState<boolean[]>([]);
  const [collapsedHeights, setCollapsedHeights] = useState<number[]>([]);
  const [expandedHeights, setExpandedHeights] = useState<number[]>([]);
  const [collapsingIndex, setCollapsingIndex] = useState<number | null>(null);

  useEffect(() => {
    // Laad altijd vanuit JSON file, niet localStorage
    fetch('/reviews.json')
      .then(response => response.json())
      .then(data => setReviewData(data))
      .catch(error => {
        console.error('Error loading reviews:', error);
        // Fallback to default reviews if JSON fails to load
        setReviewData({
          reviews: [{
            name: 'Marianne K.',
            rating: 5,
            text: 'Supergoede service! Lex denkt echt met je mee en regelt alles tot in de puntjes. Bij schade direct geholpen, geen gedoe. Echte aanrader!',
            date: '1 week geleden'
          }, {
            name: 'Peter van D.',
            rating: 5,
            text: 'Al jarenlang klant en nog nooit teleurgesteld. Persoonlijk contact, duidelijke uitleg en altijd bereikbaar. Zo hoort een verzekeringsadviseur te zijn.',
            date: '2 weken geleden'
          }, {
            name: 'Sandra M.',
            rating: 5,
            text: 'Fantastische hulp bij het regelen van onze hypotheek én verzekeringen. Alles onder één dak, veel tijd bespaard. Heel tevreden!',
            date: '3 weken geleden'
          }, {
            name: 'Mark J.',
            rating: 5,
            text: 'Top service! Snelle afhandeling van mijn autoverzekering en veel goedkoper dan mijn vorige verzekeraar. Bedankt Compleet Verzekerd!',
            date: '1 maand geleden'
          }],
          totalReviews: 236,
          averageRating: 4.9,
          lastUpdated: '2024-12-07'
        });
      });
  }, []);

  useEffect(() => {
    const results = (reviewData.reviews as any[] || []).map(r => {
      const text = (r as any).text || '';
      const wordCount = text.trim().split(/\s+/).length;
      return wordCount > 32; // meer dan ~4 regels
    });
    setCanExpand(results);
  }, [reviewData]);

  // Meet de exacte hoogtes per review zodat er niets wordt afgekapt
  useEffect(() => {
    const measure = () => {
      const newCollapsed: number[] = [];
      const newExpanded: number[] = [];
      
      textRefs.current.forEach(el => {
        if (!el) {
          newCollapsed.push(0);
          newExpanded.push(0);
          return;
        }
        
        const prevClamp = (el.style as any).WebkitLineClamp;
        const prevDisplay = el.style.display;
        
        (el.style as any).WebkitLineClamp = '';
        el.style.display = '';
        
        const style = window.getComputedStyle(el);
        const lineHeight = parseFloat(style.lineHeight || '24');
        const collapsed = Math.ceil(lineHeight * 4 + 2);
        const expanded = el.scrollHeight;
        
        if (prevClamp) {
          (el.style as any).WebkitLineClamp = prevClamp;
        } else {
          (el.style as any).WebkitLineClamp = '';
        }
        el.style.display = prevDisplay;
        
        newCollapsed.push(collapsed);
        newExpanded.push(expanded);
      });
      
      setCollapsedHeights(newCollapsed);
      setExpandedHeights(newExpanded);
    };
    
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [reviewData]);

  const toggleExpanded = (index: number) => {
    setExpandedIndex(prev => {
      if (prev === index) {
        setCollapsingIndex(index);
        return null;
      }
      if (prev !== null) {
        setCollapsingIndex(prev);
      }
      return index;
    });
  };

  const parseDutchDate = (dateStr: string): Date => {
    if (!dateStr) return new Date(0);
    
    const s = dateStr.trim().toLowerCase();
    const now = new Date();
    
    if (s === 'vandaag') return now;
    if (s === 'gisteren') return new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
    
    const rel = s.match(/(\d+)\s+(minuten?|uur|uren|dagen?|weken?|maanden?|jaren?)\s+geleden/);
    if (rel) {
      const n = parseInt(rel[1], 10);
      const unit = rel[2];
      const d = new Date(now);
      
      if (unit.startsWith('minuut') || unit.startsWith('minuten')) {
        d.setMinutes(d.getMinutes() - n);
        return d;
      }
      if (unit === 'uur' || unit === 'uren') {
        d.setHours(d.getHours() - n);
        return d;
      }
      if (unit.startsWith('dag')) {
        d.setDate(d.getDate() - n);
        return d;
      }
      if (unit.startsWith('week')) {
        d.setDate(d.getDate() - n * 7);
        return d;
      }
      if (unit.startsWith('maand')) {
        d.setMonth(d.getMonth() - n);
        return d;
      }
      if (unit.startsWith('jaar') || unit.startsWith('jaren')) {
        d.setFullYear(d.getFullYear() - n);
        return d;
      }
    }
    
    const dmy = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
    if (dmy) {
      const day = parseInt(dmy[1], 10);
      const month = parseInt(dmy[2], 10) - 1;
      const year = parseInt(dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3], 10);
      return new Date(year, month, day);
    }
    
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) return d;
    }
    
    const fallback = new Date(dateStr);
    return isNaN(fallback.getTime()) ? new Date(0) : fallback;
  };

  return (
    <section className="py-12 bg-background" aria-labelledby="reviews-heading">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" alt="Google logo" className="h-8 mr-3" />
            <span className="text-2xl font-bold text-secondary">Reviews</span>
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center space-x-1 mr-3" role="img" aria-label="5 sterren waardering">
              {[1, 2, 3, 4, 5].map(star => (
                <Star key={star} className="w-6 h-6 fill-accent text-accent" aria-hidden="true" />
              ))}
            </div>
            <span className="text-xl font-semibold text-secondary">{reviewData.averageRating} van 5</span>
          </div>
          
          <p id="reviews-heading" className="text-muted-foreground">Gebaseerd op {reviewData.totalReviews} Google reviews</p>
        </div>

        <div className="grid items-start md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" role="list">
          {[...reviewData.reviews]
            .sort((a, b) => parseDutchDate(b.date).getTime() - parseDutchDate(a.date).getTime())
            .slice(0, 4)
            .map((review, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <Card key={index} className="p-6 hover:shadow-card flex flex-col" role="listitem">
                  <article className="flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mr-3" aria-hidden="true">
                        <span className="text-primary-foreground font-semibold">
                          {review.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-secondary">{review.name}</div>
                        <div className="flex items-center" role="img" aria-label={`${review.rating} van 5 sterren`}>
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'fill-accent text-accent' : 'text-gray-300'}`} aria-hidden="true" />
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-grow mb-4">
                      <div 
                        className="transition-[max-height] duration-700 ease-in-out overflow-hidden" 
                        style={{
                          maxHeight: isExpanded 
                            ? `${expandedHeights[index] || textRefs.current[index]?.scrollHeight || 1000}px` 
                            : `${collapsedHeights[index] || 104}px`
                        }}
                        onTransitionEnd={(e) => {
                          if (e.currentTarget !== e.target) return;
                          if (collapsingIndex === index) {
                            setCollapsingIndex(null);
                          }
                        }}
                      >
                        <blockquote 
                          ref={(el) => {
                            textRefs.current[index] = el;
                          }} 
                          className="text-muted-foreground leading-relaxed" 
                          style={!isExpanded && collapsingIndex !== index ? {
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          } as any : undefined}
                        >
                          "{review.text}"
                        </blockquote>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-auto">
                      <time className="text-sm text-muted-foreground" dateTime={review.date}>
                        {review.date}
                      </time>
                      
                      {canExpand[index] && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleExpanded(index)} 
                          className="p-0 h-auto text-primary hover:text-primary-dark text-sm" 
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? (
                            <>
                              Toon minder <ChevronUp className="w-4 h-4 ml-1" />
                            </>
                          ) : (
                            <>
                              Lees meer <ChevronDown className="w-4 h-4 ml-1" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </article>
                </Card>
              );
            })}
        </div>

        <div className="text-center mb-12">
          <a 
            href="https://www.google.com/search?newwindow=1&sa=X&sca_esv=791d36d7831626ad&tbm=lcl&sxsrf=AE3TifNoSXIRXZ225rdvk7wqcdZbZaO8IA:1757269197948&q=Compleet+Verzekerd+Reviews&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxIxNDAxNrMwsjA0NTQ3sTQwBOENjIyvGKWc83MLclJTSxTCUouqUrNTi1IUglLLMlPLixex4pEEABfIusFVAAAA&rldimm=10436828151749014901&hl=nl-NL&ved=2ahUKEwj41MXyocePAxXV1QIHHaNrCpQQ9fQKegQIZBAF&cshid=1757269204141233&biw=3000&bih=1608&dpr=2#lkt=LocalPoiReviews" 
            className="inline-flex items-center text-primary hover:text-primary-dark font-semibold focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-2 py-1" 
            target="_blank" 
            aria-label="Bekijk alle reviews op Google (opent in nieuw venster)"
          >
            Bekijk alle reviews op Google
            <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsOnly;